import { useContext } from "react";

import { ParkAvatarDeprecated, UserToolbarMenuButton } from "@fleet/ui";
import { icons, useIsMobile } from "@superweb/ui";
import { useLocale } from "@superweb/intl";
import { useFlag } from "@superweb/flags";

import { useQuery } from "@fleet/api";

import { useMessage } from "#internal/intl";
import { AppLocaleContext } from "#internal/app-locale";
import { authWithPassport } from "#internal/auth/passport";

import { useAvailableLanguages } from "./language-selector";
import { useOperationIdContext } from "./operation-id-context";
import { useColorScheme } from "./color-scheme-provider";
import { searchParamTypes as t, useSearchParams } from "#internal/router-new";

const useItems = () => {
  const flag = useFlag();
  const message = useMessage();
  const locale = useLocale();
  const { setAppLocale } = useContext(AppLocaleContext);
  const { theme, setTheme } = useColorScheme();

  const availableLanguages = useAvailableLanguages();

  return {
    language: {
      type: "submenu" as const,
      icon: <icons.PlanetOutline />,
      label: message({
        id: "2d65e198-d22f-4ae7-927b-e5e1773b5add",
        context: "User menu - language button label",
        default: "Language",
      }),
      items: Object.entries(availableLanguages).map(([lang, label]) => {
        return {
          label,
          type: "checkbox" as const,
          selected: locale.toString() === lang,

          onPress: () => {
            // TODO add logging
            setAppLocale(new Intl.Locale(lang));
          },
        };
      }),
    },
    logout: {
      type: "button" as const,
      icon: <icons.Logout />,
      label: message({
        id: "305058c9-5d27-445d-9892-2adecdcaa1d2",
        context: "User menu - logout button label",
        default: "Log out",
      }),
      onPress: () => {
        // TODO add logging
        authWithPassport();
      },
    },
    theme: flag({
      id: "5548e5d7-e621-4d57-8e4a-95b276eaa8aa",
      description: "Enable theme selector",
    })
      ? {
          type: "submenu" as const,
          icon: <icons.PictureOutline />,
          label: message({
            id: "4a7e75ea-5daa-4afe-9f2c-86df6320567e",
            context: "User menu - theme button label",
            default: "Theme",
          }),
          items: [
            {
              type: "checkbox" as const,
              label: message({
                id: "a65f3a0a-e6b8-413f-99a1-fad0f4a216ed",
                context: "User menu - light theme button label",
                default: "Light",
              }),
              selected: theme === "light",
              onPress: () => {
                setTheme("light");
              },
            },
            {
              type: "checkbox" as const,
              label: message({
                id: "c7fa9df4-cd03-43f8-9c39-09886cd082c8",
                context: "User menu - dark theme button label",
                default: "Dark",
              }),
              selected: theme === "dark",
              onPress: () => {
                setTheme("dark");
              },
            },
            {
              type: "checkbox" as const,
              label: message({
                id: "428dd433-77de-42c1-a9fe-c0c3a1c4daf5",
                context: "User menu - system theme button label",
                default: "System",
              }),
              selected: theme === "system",
              onPress: () => {
                setTheme("system");
              },
            },
          ],
        }
      : undefined,
  };
};

export const MenuButton = () => {
  const locale = useLocale();
  const message = useMessage();
  const isMobile = useIsMobile();
  const { language, theme } = useItems();
  const { sessionId } = useOperationIdContext();
  const [{ ref_id: refId }] = useSearchParams({ ref_id: t.string });

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId!,
    },
  });

  const parkInfo = response.status === 200 ? response.body : undefined;
  const parkName =
    parkInfo?.partner_name ??
    message({
      id: "bba961e2-d9ee-4bc6-aac1-0b1e84e2007a",
      context: "Park button. Label",
      default: "Fleet",
    });

  return (
    <UserToolbarMenuButton
      icon={
        <ParkAvatarDeprecated
          id={parkName}
          name={parkName}
          size={isMobile ? "l" : "m"}
        />
      }
      label={isMobile ? undefined : parkName}
      // TODO add logging
      onOpenChange={() => {}}
      items={[
        {
          type: "group",
          items: [language, theme],
        },
      ]}
    />
  );
};
